import React from "react";

export interface ExamFooterUIProps {
  // Student info
  studentName: string;

  // Question navigation
  currentQuestionIdx: number;
  totalQuestions: number;
  sectionTitle?: string;

  // Navigation buttons
  disablePrev?: boolean;
  disableNext?: boolean;
  nextButtonText?: string;
  showQuestionSelector?: boolean;

  // Question states (for popover)
  answeredQuestions?: number[];
  forReviewQuestions?: number[];

  // Mode-specific
  mode?: "exam" | "result" | "demo";
  isSubmitting?: boolean;
  showReviewButton?: boolean;

  // Event handlers
  onPrev?: () => void;
  onNext?: () => void;
  onQuestionSelect?: (idx: number) => void;
  onReviewClick?: () => void;

  // Question selector popover
  questionSelectorOpen?: boolean;
  onToggleQuestionSelector?: () => void;
  questionSelectorContent?: React.ReactNode;
}

const ExamFooterUI: React.FC<ExamFooterUIProps> = ({
  studentName,
  currentQuestionIdx,
  totalQuestions,
  sectionTitle = "Section",
  disablePrev = false,
  disableNext = false,
  nextButtonText = "Next",
  showQuestionSelector = true,
  answeredQuestions = [],
  forReviewQuestions = [],
  mode = "exam",
  isSubmitting = false,
  showReviewButton = false,
  onPrev,
  onNext,
  onQuestionSelect,
  onReviewClick,
  questionSelectorOpen = false,
  onToggleQuestionSelector,
  questionSelectorContent,
}) => {
  const isReviewMode = mode === "result";

  return (
    <div className="flex items-center justify-between px-8 py-3 bg-[#e6edf8] custom-dashed-border-top relative">
      {/* Left: Student name */}
      <div className="text-sm text-gray-800 font-bold font-sans">
        {studentName}
      </div>

      {/* Center: Question selector */}
      <div className="absolute left-1/2 -translate-x-1/2">
        {showQuestionSelector && (
          <>
            <button
              className="bg-black text-white font-bold rounded-md px-6 py-2 text-base shadow text-center select-none cursor-pointer"
              style={{ minWidth: 160 }}
              onClick={onToggleQuestionSelector}
            >
              Question {currentQuestionIdx + 1} of {totalQuestions}
            </button>
            {questionSelectorContent}
          </>
        )}
        {!showQuestionSelector && (
          <div className="text-sm font-medium text-gray-600">
            Question {currentQuestionIdx + 1} of {totalQuestions}
          </div>
        )}
      </div>

      {/* Right: Navigation buttons */}
      <div className="flex gap-3">
        {/* Back button */}
        {!disablePrev && onPrev && (
          <button
            className="bg-[#3451d6] hover:bg-[#22336b] text-white rounded-full px-6 py-2 font-bold text-base shadow transition-all duration-150 ease-in-out transform hover:scale-[1.02] active:scale-[0.95] cursor-pointer"
            onClick={onPrev}
          >
            Back
          </button>
        )}

        {/* Review button (for demo mode) */}
        {showReviewButton && onReviewClick && (
          <button
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6 py-2 font-bold text-base shadow transition-all duration-150 ease-in-out transform hover:scale-[1.02] active:scale-[0.95] cursor-pointer"
            onClick={onReviewClick}
          >
            Review
          </button>
        )}

        {/* Next button */}
        {onNext && (
          <button
            className="bg-[#3451d6] hover:bg-[#22336b] text-white rounded-full px-6 py-2 font-bold text-base shadow transition-all duration-150 ease-in-out transform hover:scale-[1.02] active:scale-[0.95] cursor-pointer disabled:opacity-50"
            disabled={disableNext || isSubmitting}
            onClick={onNext}
          >
            {isSubmitting ? "Submitting..." : nextButtonText}
          </button>
        )}
      </div>
    </div>
  );
};

export default ExamFooterUI;
